import { EX } from "../data/exercises";
import {
  EASIER,
  FLAGS,
  HARDER,
  HI,
  TAGS,
  VIDEO,
  WHY,
  YOGA,
} from "./exerciseMeta";
import { doseIndex, doseText, items, sessionFor, sessionKeyFor } from "./plan";
import type { Exercise, ExerciseId, ExerciseKind, KayaState, MoveDose, MoveFlag, MoveTag } from "./types";

/**
 * The hand-written move library: 62 moves for an older body (legs, balance, a seated warm-up,
 * spine-safe upper body and core, gentle cardio, stretches, yoga and breath), in the generated
 * `Exercise` shape plus the fields the generated 48 never carried. (ADR-051)
 *
 * They live here and not in `data/exercises.ts` because that file is the reference PWA's,
 * regenerated by `scripts/extract-data.js` (ADR-001). Every move, its `how` and its `wrong` come
 * from `docs/research/2026-10-05/move-library-older-adults.md` §2.2–§2.7, in that report's
 * wording; the doses are its beginner · intermediate · established figures, which a template
 * turns into weeks 1–2, 3–4 and 5+. The last four are the morning yoga session's (ADR-067),
 * written in the same voice from §2.7 and the Common Yoga Protocol it cites.
 *
 * `w` is where the move happens in the words a row shows: "chair", "at the counter" (standing,
 * a hand on a counter or a chair back), "standing", "wall" or "bed".
 */

const yt = (q: string) =>
  "https://www.youtube.com/results?search_query=" + encodeURIComponent(q + " exercise for older adults");

type Raw = Omit<Exercise, "id" | "yt">;

const RAW: Record<ExerciseId, Raw> = {
  /* ---------- warm-up and joints, all seated (§2.2) ---------- */
  neckturn: {
    n: "Neck turns", w: "chair", t: "mobility",
    how: "Sit tall, shoulders low. Turn the head slowly to look over one shoulder, pause, come back through the middle, then the other way.",
    wrong: ["Chin tipping up or down as you turn", "Forcing the last bit — stop where it is easy"],
    tags: ["chair", "neck"], flags: ["dizzy"],
    why: "so looking over your shoulder to cross a road stays easy",
    hi: "गर्दन घुमाना", dose: { r: [3, 5, 5], side: true },
  },
  chintuck: {
    n: "Chin tuck", w: "chair", t: "mobility",
    how: "Sit tall and look ahead. Glide the chin straight back, as if making a double chin, hold, let go.",
    wrong: ["Nodding down instead of gliding back", "Tipping the head back"],
    tags: ["chair", "neck"],
    why: "so the head sits over the shoulders instead of poking forward",
    hi: "ठोड़ी पीछे खींचना", dose: { r: [5, 8, 10] },
  },
  shroll: {
    n: "Shoulder rolls", w: "chair", t: "mobility",
    how: "Arms loose. Lift the shoulders up, draw them back, let them drop, in slow circles; then reverse.",
    wrong: ["Rushing", "Chin poking forward"],
    tags: ["chair", "shoulder"], harder: "armcircles",
    why: "so stiff shoulders loosen before anything asks more of them",
    hi: "कंधे घुमाना", dose: { r: [8, 10, 10] },
  },
  armraise: {
    n: "Seated arm raises", w: "chair", t: "upper",
    how: "Sit tall, feet flat. Raise both arms forward and up as high as is comfortable, breathing in; lower breathing out.",
    wrong: ["Arching the back to get higher", "Shrugging"],
    tags: ["chair", "shoulder"],
    why: "so reaching up to a shelf stays easy",
    hi: "बैठकर हाथ ऊपर उठाना", dose: { r: [5, 8, 10] },
  },
  ankle: {
    n: "Ankle pumps and circles", w: "chair", t: "mobility",
    how: "One leg out, heel on the floor. Point the toes away, pull them back towards you, then draw slow circles each way.",
    wrong: ["Moving the whole leg", "Going fast"],
    tags: ["chair"], harder: "calfraise",
    why: "so the ankles are ready to balance on, and swollen ankles drain",
    hi: "टखने घुमाना", dose: { r: [10, 10, 15], side: true },
  },
  trunkturn: {
    n: "Seated trunk turn", w: "chair", t: "mobility",
    how: "Arms crossed on the chest. Turn the shoulders slowly to one side as far as is easy, hips and knees still facing forward; return; switch.",
    wrong: ["Hauling round on the chair back", "Slumping as you turn"],
    tags: ["chair", "rotation"], flags: ["osteoporosis"],
    why: "so turning to reach behind you in the car stays easy",
    hi: "बैठकर कमर घुमाना", dose: { r: [5, 5, 8], side: true },
  },
  backext: {
    n: "Seated back extension", w: "chair", t: "mobility",
    how: "Sit on the front of the chair, hands on the lower back. Lift the chest and look slightly up, arching gently back, then return to tall.",
    wrong: ["Throwing the head back", "Leaning on the chair back"],
    tags: ["chair"], flags: ["dizzy"],
    why: "so the upper back stays straight rather than rounding forward",
    hi: "बैठकर पीठ पीछे झुकाना", dose: { r: [5, 5, 8] },
  },
  seatmarch: {
    n: "Seated march", w: "chair", t: "cardio",
    how: "Hold the sides of the seat, sit tall. Lift one knee a few inches, lower with control, then the other; swing the arms once steady.",
    wrong: ["Leaning back to lift higher", "Slumping"],
    tags: ["chair", "hip"], flags: ["hipReplacement"], harder: "march",
    why: "so the heart and the hips warm up before you stand",
    hi: "बैठकर कदमताल", dose: { t: [30, 60, 90] },
  },

  /* ---------- legs (§2.3) ---------- */
  sitstand: {
    n: "Sit-to-stand", w: "chair", t: "legs",
    how: "Sit on the front half of a firm chair, feet just behind the knees. Lean forward, nose over toes, and stand through the heels; sit back down slowly and touch the seat lightly.",
    wrong: ["Dropping into the seat", "Knees falling inward", "Pushing off with the hands once you don't need to"],
    tags: ["chair", "knee", "hip"], flags: ["knee", "hipReplacement"], easier: "kneeext", harder: "stepup",
    why: "so getting up from a chair, a bed or the toilet stays easy",
    hi: "कुर्सी से उठना-बैठना", dose: { r: [5, 10, 12] },
  },
  minisquat: {
    n: "Mini squat at the counter", w: "at the counter", t: "legs",
    how: "Both hands on the counter, feet hip-width. Bend the knees a little and push the hips back as if starting to sit, then stand tall and squeeze the buttocks.",
    wrong: ["Bending too deep", "Heels lifting", "Knees drifting in"],
    tags: ["support", "knee", "hip"], flags: ["knee", "balance"], easier: "sitstand", harder: "squat",
    why: "so the thighs can lower you onto a seat or a step under control",
    hi: "काउंटर पकड़कर हल्की उठक-बैठक", dose: { r: [5, 10, 12] },
  },
  calfraise: {
    n: "Heel raises", w: "at the counter", t: "legs",
    how: "Face the counter, hands on it. Rise onto the balls of the feet, pause, lower slowly.",
    wrong: ["Leaning onto the counter", "Rolling to the outside of the feet"],
    tags: ["support", "balance"], flags: ["balance"], easier: "ankle",
    why: "so each step pushes off properly and the ankles catch a stumble",
    hi: "एड़ी उठाना", dose: { r: [10, 15, 20] },
  },
  toeraise: {
    n: "Toe raises", w: "at the counter", t: "legs",
    how: "At the counter. Lift the toes and fronts of the feet, rocking onto the heels, then lower.",
    wrong: ["Sticking the bottom out to stay up", "Rushing"],
    tags: ["support", "balance"], flags: ["balance"],
    why: "so the toes clear the ground and a rug edge does not trip you",
    hi: "पंजे उठाना", dose: { r: [10, 15, 20] },
  },
  sideleg: {
    n: "Side leg raise", w: "at the counter", t: "legs",
    how: "Hold the counter, stand tall. Lift one leg out to the side, toes forward, hold a moment, lower slowly.",
    wrong: ["Leaning away from the leg", "Toes turning up"],
    tags: ["support", "hip", "balance"], flags: ["balance", "hipReplacement"], easier: "clam",
    why: "so the hips hold you level when you stand on one foot to step",
    hi: "पैर बगल में उठाना", dose: { r: [5, 10, 12], side: true },
  },
  kneecurl: {
    n: "Standing knee curl", w: "at the counter", t: "legs",
    how: "Hold the counter. Bend one knee to bring the heel up behind you, thighs side by side; lower slowly.",
    wrong: ["Thigh swinging forward", "Arching the back"],
    tags: ["support", "knee", "balance"], flags: ["balance"],
    why: "so the backs of the thighs share the work the knees do",
    hi: "खड़े होकर घुटना मोड़ना", dose: { r: [5, 10, 12], side: true },
  },
  kneeext: {
    n: "Seated knee straightening", w: "chair", t: "legs",
    how: "Sit back in the chair. Straighten one knee until the leg is level, pull the toes up, hold, lower slowly.",
    wrong: ["Leaning back", "Swinging the leg up"],
    tags: ["chair", "knee"], flags: ["knee"], easier: "quadset",
    why: "so the thigh that protects a sore knee grows stronger",
    hi: "बैठकर घुटना सीधा करना", dose: { r: [5, 10, 12], side: true },
  },
  quadset: {
    n: "Quads set", w: "bed", t: "legs",
    how: "Sit or lie with the legs long, a rolled towel under one knee. Tighten the front of the thigh so the knee presses into the towel; hold, relax.",
    wrong: ["Holding the breath", "Lifting the heel without the thigh working"],
    tags: ["bed", "knee", "isometric"], harder: "slr",
    why: "so a painful or operated knee gets its thigh working again",
    hi: "जाँघ कसना", dose: { r: [10, 10, 15] },
  },
  slr: {
    n: "Straight-leg raise", w: "bed", t: "legs",
    how: "On your back, one knee bent and that foot flat. Tighten the other thigh, lift the straight leg to the height of the bent knee, hold, lower slowly.",
    wrong: ["Knee bending as it lifts", "Back arching off the bed"],
    tags: ["bed", "knee", "hip"], flags: ["hipReplacement"], easier: "quadset",
    why: "so the thigh grows stronger without bending a sore knee",
    hi: "सीधा पैर उठाना", dose: { r: [5, 10, 12], side: true },
  },
  stepup: {
    n: "Step-up", w: "at the counter", t: "legs",
    how: "Face the bottom stair, hand on the rail. Step up with one foot, straighten that leg to bring the other up, step down leading with the same foot.",
    wrong: ["Pushing off the back foot", "Knee caving in", "Looking at your feet"],
    tags: ["support", "knee", "hip", "balance"], flags: ["knee", "balance"], easier: "sitstand",
    why: "so stairs and kerbs stay easy",
    hi: "सीढ़ी पर चढ़ना-उतरना", dose: { r: [5, 8, 10], side: true },
  },
  wallsit: {
    n: "Wall sit, shallow", w: "wall", t: "legs",
    how: "Back flat on the wall, feet a step forward. Slide down until the knees are only slightly bent and hold, counting out loud; slide back up.",
    wrong: ["Holding the breath", "Sliding so low the knees hurt", "Knees past the toes"],
    tags: ["wall", "knee", "isometric"], flags: ["knee"], easier: "quadset",
    why: "so the thighs grow stronger — held with steady breathing, it also helps blood pressure",
    hi: "दीवार के सहारे बैठना", dose: { t: [10, 20, 30] },
  },
  clam: {
    n: "Clamshell", w: "bed", t: "legs",
    how: "Lie on your side, knees bent, heels together. Keep the feet touching and lift the top knee like a shell opening, then lower.",
    wrong: ["Rolling the hips back", "Feet lifting apart"],
    tags: ["bed", "hip"], flags: ["hipReplacement"], harder: "sideleg",
    why: "so the side of the hip holds you steady on one leg",
    hi: "करवट लेकर घुटना खोलना", dose: { r: [8, 10, 15], side: true },
  },
  hipext: {
    n: "Standing hip extension", w: "at the counter", t: "legs",
    how: "Hold the counter, stand tall. Take one straight leg back a few inches, squeeze the buttock, return.",
    wrong: ["Leaning forward", "Arching the lower back"],
    tags: ["support", "hip", "balance"], flags: ["balance", "hipReplacement"],
    why: "so the buttocks drive each step and you stand up straight",
    hi: "खड़े होकर पैर पीछे ले जाना", dose: { r: [8, 10, 12], side: true },
  },
  heelslide: {
    n: "Heel slides", w: "bed", t: "legs",
    how: "On your back, knees bent, tummy gently braced. Slide one heel away until the leg is straight, slide it back, back staying still.",
    wrong: ["Back arching", "Holding the breath"],
    tags: ["bed", "hip", "knee"], flags: ["hipReplacement"], harder: "deadbug",
    why: "so the knee and hip bend freely first thing in the morning",
    hi: "एड़ी खिसकाना", dose: { r: [6, 10, 12], side: true },
  },
  heeldig: {
    n: "Heel digs", w: "chair", t: "cardio",
    how: "Sit tall. Tap one heel forward on the floor and back, then the other, arms swinging in time.",
    wrong: ["Leaning back", "Locking the knee"],
    tags: ["chair", "knee"],
    why: "so the heart works a little without standing up",
    hi: "बैठकर एड़ी टिकाना", dose: { t: [30, 45, 60] },
  },

  /* ---------- balance (§2.4): every move starts at the counter ---------- */
  weightshift: {
    n: "Weight shifts", w: "at the counter", t: "balance",
    how: "Hands on the counter, feet hip-width. Move the weight slowly onto one foot until the other feels light, hold, then shift across; then front to back.",
    wrong: ["Bending at the waist rather than moving from the ankles", "Lifting the foot too soon"],
    tags: ["support", "balance"], flags: ["balance"], harder: "semitandem",
    why: "so your body learns where its balance point is before it is tested",
    hi: "वज़न एक पैर से दूसरे पर", dose: { r: [5, 8, 10], side: true },
  },
  semitandem: {
    n: "Staggered stand", w: "at the counter", t: "balance",
    how: "At the counter. Put one foot half a step ahead so the heel sits beside the other foot's big toe; stand tall and hold. Swap.",
    wrong: ["Looking down", "Locking the knees"],
    tags: ["support", "balance"], flags: ["balance", "dizzy"], easier: "weightshift", harder: "tandemstand",
    why: "so you stay steady when the ground is narrow or someone brushes past",
    hi: "आगे-पीछे पैर रखकर खड़े होना", dose: { t: [10, 20, 30], side: true },
  },
  tandemstand: {
    n: "Heel-to-toe stand", w: "at the counter", t: "balance",
    how: "At the counter. Place one foot directly in front of the other, heel touching toes, and hold. Swap.",
    wrong: ["Looking at your feet", "Leaning on the counter"],
    tags: ["support", "balance"], flags: ["balance", "dizzy"], easier: "semitandem", harder: "onelegstand",
    why: "so a narrow path or a turn in a small kitchen does not tip you",
    hi: "एड़ी से पंजा मिलाकर खड़े होना", dose: { t: [10, 20, 30], side: true },
  },
  onelegstand: {
    n: "One-leg stand", w: "at the counter", t: "balance",
    how: "Hold the counter, stand tall. Lift one foot just off the floor and hold; swap.",
    wrong: ["Leaning into the support", "Hitching the hip"],
    tags: ["support", "balance"], flags: ["balance", "dizzy"], easier: "tandemstand",
    why: "so putting on trousers or stepping into a bus stays safe",
    hi: "एक पैर पर खड़े होना", dose: { t: [5, 10, 30], side: true },
  },
  tandemwalk: {
    n: "Heel-to-toe walk", w: "wall", t: "balance",
    how: "Side-on to a wall, fingertips on it. Walk forward with each heel landing just ahead of the other toes, ten steps; turn slowly and come back.",
    wrong: ["Rushing", "Arms flailing — slow down"],
    tags: ["wall", "balance"], flags: ["balance", "dizzy"], easier: "tandemstand",
    why: "so you keep your line on a narrow or crowded path",
    hi: "एड़ी से पंजा मिलाकर चलना", dose: { r: [5, 10, 10] },
  },
  sidewalk: {
    n: "Sideways walking", w: "at the counter", t: "balance",
    how: "Along the counter, knees soft. Step to the side, bring the other foot to meet it; ten steps, then back.",
    wrong: ["Crossing the feet", "Leaning forward"],
    tags: ["support", "hip", "balance"], flags: ["balance", "hipReplacement"], easier: "weightshift", harder: "grapevine",
    why: "so a sudden step to the side catches you instead of tripping you",
    hi: "बगल में चलना", dose: { r: [10, 10, 15] },
  },
  grapevine: {
    n: "Grapevine", w: "at the counter", t: "balance",
    how: "Hand on the counter. Step to the side, cross the next foot behind, step to the side, then cross in front; slowly, then come back.",
    wrong: ["Twisting the knee — let the hips turn a little", "Speed"],
    tags: ["support", "hip", "balance"], flags: ["hipReplacement", "balance"], easier: "sidewalk",
    why: "so the feet stay quick and sure when they have to cross",
    hi: "पैर क्रॉस करके बगल में चलना", dose: { r: [5, 10, 10] },
  },
  backwalk: {
    n: "Walking backwards", w: "at the counter", t: "balance",
    how: "Hand on the counter or on someone beside you. Walk backwards ten steps, toes then heels, looking ahead; turn and walk forward.",
    wrong: ["Looking over the shoulder", "Shuffling"],
    tags: ["support", "balance"], flags: ["balance", "dizzy"], easier: "weightshift",
    why: "so stepping back from a door or a kerb does not catch you out",
    hi: "उल्टा चलना", dose: { r: [10, 10, 15] },
  },
  figure8: {
    n: "Walk and turn", w: "standing", t: "balance",
    how: "Walk slowly in a figure of eight round two chairs, turning in small steps.",
    wrong: ["Pivoting on one foot", "Rushing the turn"],
    tags: ["standing", "balance"], flags: ["balance", "dizzy"], easier: "sidewalk",
    why: "so turning round in a small room stays steady",
    hi: "आठ के आकार में चलना", dose: { r: [2, 2, 3] },
  },
  heeltoewalk: {
    n: "Heel walk, toe walk", w: "wall", t: "balance",
    how: "Hand along a wall. Ten steps on the heels with the toes lifted, then ten on the toes with the heels high.",
    wrong: ["Bottom sticking out", "Big steps"],
    tags: ["wall", "balance"], flags: ["balance"], easier: "toeraise",
    why: "so the shins and calves keep the feet clear of the ground",
    hi: "एड़ी पर, फिर पंजों पर चलना", dose: { r: [10, 10, 10] },
  },
  tree: {
    n: "Tree pose with support", w: "at the counter", t: "balance",
    how: "Hold the counter. Rest one sole against the other ankle, toes on the floor; slide it to the calf only if steady. Hands to the chest only when steady.",
    wrong: ["Foot pressed on the knee", "Leaning on the counter"],
    tags: ["support", "hip", "balance"], flags: ["knee", "hipReplacement", "balance"], easier: "tandemstand",
    why: "so standing on one leg feels calm rather than a wobble",
    hi: "वृक्षासन", yoga: "Vrikshasana", dose: { t: [10, 20, 30], side: true },
  },
  tadasana: {
    n: "Mountain pose, rising", w: "at the counter", t: "balance",
    how: "Stand tall, feet hip-width, one hand on the counter. Reach the free arm up and rise onto the toes as you breathe in; lower as you breathe out.",
    wrong: ["Rising before you are steady", "Tipping forward"],
    tags: ["support", "balance"], flags: ["balance"], easier: "calfraise",
    why: "so standing tall and reaching up stay steady",
    hi: "ताड़ासन", yoga: "Tadasana", dose: { r: [5, 8, 10] },
  },

  /* ---------- upper body, back and core, spine-safe (§2.5) ---------- */
  armcurl: {
    n: "Arm curl with bottles", w: "chair", t: "upper",
    how: "Sit tall, a filled water bottle in each hand, palms up. Bend the elbows to bring the bottles to the shoulders; lower slowly.",
    wrong: ["Swinging the body", "Elbows drifting forward"],
    tags: ["chair", "wrist"],
    why: "so carrying shopping or a grandchild stays easy",
    hi: "बोतल से हाथ मोड़ना", dose: { r: [8, 10, 12] },
  },
  press: {
    n: "Overhead press", w: "chair", t: "upper",
    how: "Sit tall, bottles at shoulder height, palms forward. Press up until the arms are nearly straight, breathing out; lower slowly.",
    wrong: ["Arching the back", "Shrugging"],
    tags: ["chair", "shoulder"], easier: "armraise",
    why: "so lifting something onto a high shelf stays easy",
    hi: "बोतल ऊपर उठाना", dose: { r: [5, 8, 10] },
  },
  row: {
    n: "Seated row", w: "chair", t: "upper",
    how: "Band looped round the feet or a door handle; sit tall and draw the elbows back along the ribs, shoulder blades together; return slowly. No band: pull a towel apart hard.",
    wrong: ["Leaning back to pull", "Shrugging"],
    tags: ["chair", "shoulder"], easier: "scapsqueeze",
    why: "so the upper back holds you upright",
    hi: "बैठकर खींचना", dose: { r: [8, 10, 12] },
  },
  scapsqueeze: {
    n: "Shoulder-blade squeeze", w: "chair", t: "upper",
    how: "Sit tall, elbows bent at your sides. Draw the shoulder blades back and down, hold, let go.",
    wrong: ["Shrugging up", "Arching the back"],
    tags: ["chair", "shoulder"], harder: "wallangels",
    why: "so a rounded upper back straightens a little",
    hi: "कंधे पीछे दबाना", dose: { r: [8, 10, 12] },
  },
  wallslide: {
    n: "Wall slides", w: "wall", t: "upper",
    how: "Face the wall, forearms on it, little fingers towards it. Slide the arms up the wall as far as is comfortable, then down.",
    wrong: ["Arching the lower back", "Shrugging"],
    tags: ["wall", "shoulder"], easier: "armraise",
    why: "so the arms still reach overhead to dress and wash your hair",
    hi: "दीवार पर हाथ सरकाना", dose: { r: [5, 8, 10] },
  },
  pendulum: {
    n: "Pendulum", w: "at the counter", t: "mobility",
    how: "One hand on a table, lean forward from the hips, the other arm hanging loose. Rock the body gently so the arm swings in small circles and to and fro.",
    wrong: ["Lifting the arm with its own muscles", "Rounding the back"],
    tags: ["support", "shoulder", "balance"], flags: ["balance", "dizzy"], harder: "wallslide",
    why: "so a stiff or sore shoulder moves without effort",
    hi: "हाथ झुलाना", dose: { t: [30, 30, 60], side: true },
  },
  towelreach: {
    n: "Towel behind the back", w: "standing", t: "mobility",
    how: "A towel behind you, one hand over the shoulder, one at the waist. The top hand gently draws the lower hand up; then the lower draws the top down.",
    wrong: ["Forcing", "Arching the back"],
    tags: ["standing", "shoulder"],
    why: "so fastening, tucking in and washing your back stay possible",
    hi: "पीठ के पीछे तौलिया", dose: { t: [15, 20, 30], side: true },
  },
  towelsqueeze: {
    n: "Towel squeeze", w: "chair", t: "upper",
    how: "Roll a towel and squeeze it hard in both hands, then wring it as if drying it, breathing all the way.",
    wrong: ["Holding the breath", "Shrugging"],
    tags: ["chair", "wrist", "isometric"],
    why: "so the grip stays strong — held with steady breathing, it also helps blood pressure",
    hi: "तौलिया निचोड़ना", dose: { t: [10, 20, 30] },
  },
  wallplank: {
    n: "Wall plank", w: "wall", t: "core",
    how: "Forearms on the wall at shoulder height, walk the feet back until the body leans in one straight line. Hold, breathing steadily.",
    wrong: ["Holding the breath", "Hips sticking out"],
    tags: ["wall", "shoulder", "isometric"],
    why: "so the trunk holds you straight without getting down on the floor",
    hi: "दीवार पर प्लैंक", dose: { t: [10, 20, 30] },
  },
  wallside: {
    n: "Side lean at the wall", w: "wall", t: "core",
    how: "Side-on to the wall, forearm on it at shoulder height, feet a step away. Lean in with the body straight, press the hip away from the wall and hold.",
    wrong: ["Hip sagging to the wall", "Shoulder hunching"],
    tags: ["wall", "shoulder", "isometric"], harder: "sideplank",
    why: "so the sides of the trunk keep you upright when you carry a bag in one hand",
    hi: "दीवार पर बगल से झुकना", dose: { t: [10, 15, 20], side: true },
  },
  standbirddog: {
    n: "Standing bird dog", w: "at the counter", t: "core",
    how: "One hand on the counter. Reach the other arm forward and the opposite leg back a little, hips level; hold 3 s, return.",
    wrong: ["Twisting the hips open", "Arching the back"],
    tags: ["support", "balance"], flags: ["balance"], harder: "birddog",
    why: "so the back is steady when an arm and a leg move at once",
    hi: "खड़े होकर हाथ-पैर फैलाना", dose: { r: [5, 8, 10], side: true },
  },
  curlup: {
    n: "McGill curl-up", w: "bed", t: "core",
    how: "On your back, one knee bent, hands under the small of the back. Lift only the head and shoulders a finger's width, chin tucked, and hold.",
    wrong: ["Curling up like a sit-up", "Pulling on the neck"],
    tags: ["bed", "flexion", "neck", "isometric"], flags: ["osteoporosis", "bp"], easier: "wallplank",
    why: "so the front of the trunk works without the bending a sit-up asks",
    dose: { t: [5, 8, 10] },
  },

  /* ---------- cardio and walking (§2.6) ---------- */
  seatpunch: {
    n: "Seated punches", w: "chair", t: "cardio",
    how: "Sit tall, fists by the chin. Punch forward and a little across, one arm then the other, at a pace you can still talk through.",
    wrong: ["Locking the elbow", "Holding the breath"],
    tags: ["chair", "shoulder"], harder: "boxing",
    why: "so the heart gets going while you stay seated",
    hi: "बैठकर मुक्के", dose: { t: [30, 45, 60] },
  },
  seatjack: {
    n: "Seated arm-and-leg jacks", w: "chair", t: "cardio",
    how: "Sit on the front of the chair. Tap the feet out to the sides as the arms rise out wide; bring both back in.",
    wrong: ["Swinging the arms above comfortable height", "Slumping"],
    tags: ["chair", "shoulder", "hip"], flags: ["hipReplacement"], easier: "seatmarch",
    why: "so the whole body warms up without a jump",
    hi: "बैठकर हाथ-पैर खोलना", dose: { t: [30, 45, 60] },
  },
  walk: {
    n: "Walk", w: "standing", t: "cardio",
    how: "Walk at a pace where you can talk but not sing, on a flat even route. Split it across the day if one go is too long.",
    wrong: ["Starting too fast", "Walking on uneven ground on a dizzy day"],
    tags: ["standing", "knee", "hip", "balance"], flags: ["balance", "dizzy"],
    why: "so the heart, the blood sugar and the legs all get their daily share",
    hi: "टहलना", dose: { t: [600, 1200, 1800] }, met: 3.1,
  },

  /* ---------- stretches, yoga and breath (§2.7) ---------- */
  calfstretch: {
    n: "Calf stretch at the wall", w: "wall", t: "mobility",
    how: "Hands on the wall, one foot back, heel down, leg straight. Bend the front knee and lean in until the back calf stretches.",
    wrong: ["Back heel lifting", "Toes turned out"],
    tags: ["wall"], flags: ["balance"],
    why: "so the ankles bend enough for a full, safe stride",
    hi: "दीवार पर पिंडली खिंचाव", dose: { t: [20, 30, 30], side: true },
  },
  hamchair: {
    n: "Seated hamstring stretch", w: "chair", t: "mobility",
    how: "On the front of the chair, one leg straight with the heel down. Sit tall and lean forward from the hips, back straight, until the back of the thigh pulls.",
    wrong: ["Rounding the back to reach the toes", "Locking the knee hard"],
    tags: ["chair", "hip"], flags: ["osteoporosis", "hipReplacement"],
    why: "so the backs of the legs stay long enough to walk tall",
    hi: "बैठकर जाँघ के पीछे खिंचाव", dose: { t: [20, 30, 30], side: true },
  },
  standhipflex: {
    n: "Standing hip-flexor stretch", w: "at the counter", t: "mobility",
    how: "Hold the counter, one foot back in a short split stance. Tuck the tailbone and squeeze the back buttock until the front of the hip stretches.",
    wrong: ["Arching the lower back", "Too long a step"],
    tags: ["support", "hip", "balance"], flags: ["balance", "hipReplacement"],
    why: "so long hours of sitting do not pull you into a stoop",
    hi: "खड़े होकर कूल्हे के आगे खिंचाव", dose: { t: [20, 30, 30], side: true },
  },
  seatfig4: {
    n: "Seated figure-4", w: "chair", t: "mobility",
    how: "Sit tall, rest one ankle on the opposite knee. Lean forward a little from the hips, back straight, until the buttock stretches.",
    wrong: ["Pressing the knee down", "Rounding"],
    tags: ["chair", "hip", "knee"], flags: ["hipReplacement", "knee"], easier: "hamchair",
    why: "so the hips turn out enough to put on socks and shoes",
    hi: "बैठकर चार के आकार में खिंचाव", dose: { t: [20, 30, 30], side: true },
  },
  sidebendchair: {
    n: "Seated side bend", w: "chair", t: "mobility",
    how: "Sit tall, one hand on the seat. Reach the other arm up and over, bending the ribs to the side; return, switch.",
    wrong: ["Leaning forward", "Collapsing onto the hand"],
    tags: ["chair", "shoulder"], flags: ["osteoporosis"], harder: "sidebend",
    why: "so the sides of the trunk stay supple for reaching and turning in bed",
    hi: "अर्ध कटिचक्रासन", yoga: "Ardha Kati Chakrasana", dose: { r: [3, 5, 5], side: true },
  },
  anulom: {
    n: "Alternate-nostril breathing", w: "chair", t: "breath",
    how: "Sit tall. Close the right nostril with the thumb and breathe in through the left; close the left with the ring finger and breathe out through the right; in right, out left. Slow and quiet.",
    wrong: ["Holding the breath between — leave the hold out", "Forcing the breath"],
    tags: ["chair", "breath"], easier: "breathing",
    why: "so the breath slows, and with it the heart and the mind",
    hi: "अनुलोम विलोम", yoga: "Anulom Vilom", dose: { t: [60, 120, 180] },
  },
  bhramari: {
    n: "Humming breath", w: "chair", t: "breath",
    how: "Sit tall, eyes closed. Breathe in through the nose, then hum softly on one long out-breath until it ends by itself.",
    wrong: ["Pressing hard on the ears", "Straining to make it last"],
    tags: ["chair", "breath"], easier: "breathing",
    why: "so a long, calm out-breath settles you at the end of a session",
    hi: "भ्रामरी", yoga: "Bhramari", dose: { r: [5, 7, 9] },
  },
  shavasana: {
    n: "Rest on the back", w: "bed", t: "breath",
    how: "Lie on your back, a pillow under the knees, arms loose. Let the breath slow; to get up, roll to the side and push up with the arms.",
    wrong: ["Sitting straight up at the end", "Holding tension in the jaw"],
    tags: ["bed", "breath"], flags: ["dizzy"],
    why: "so the body settles before you get up and go on with the day",
    hi: "शवासन", yoga: "Shavasana", dose: { t: [60, 120, 180] },
  },

  /* ---------- the morning yoga and breathing session's own (ADR-067) ---------- */
  chairtadasana: {
    n: "Mountain pose, seated", w: "chair", t: "mobility",
    how: "Sit tall on the front of the chair, feet flat and hip-width. Breathe in and sweep both arms up beside the ears; breathe out and lower them.",
    wrong: ["Arching the lower back to reach higher", "Shoulders up by the ears"],
    tags: ["chair", "shoulder"],
    why: "so sitting and standing tall come without thinking",
    hi: "बैठकर ताड़ासन", yoga: "Tadasana (seated)", dose: { r: [3, 5, 5] },
  },
  foldchair: {
    n: "Seated forward fold", w: "chair", t: "mobility",
    how: "Sit on the front of the chair, feet wide. Breathe out and fold forward from the hips, hands sliding down the shins as far as is easy; breathe in and roll up slowly.",
    wrong: ["Dropping the head below the knees", "Coming up fast"],
    tags: ["chair", "flexion"], flags: ["osteoporosis", "bp"], easier: "hamchair",
    why: "so the back and the backs of the legs let go after a night's sleep",
    hi: "बैठकर आगे झुकना", dose: { r: [3, 5, 5] },
  },
  catcowchair: {
    n: "Seated cat–cow", w: "chair", t: "mobility",
    how: "Sit tall, hands on the knees. Breathe in, lift the chest and draw the shoulders back; breathe out, round the back and tuck the chin.",
    wrong: ["Moving fast", "Rounding hard at the end of the breath"],
    // the rounding half is the spinal flexion bones with osteoporosis avoid (move-library §3.1)
    tags: ["chair", "flexion"], flags: ["osteoporosis"], easier: "backext",
    why: "so the spine moves through its whole range before the day asks anything of it",
    hi: "बैठकर मार्जरी आसन", yoga: "Marjaryasana–Bitilasana (seated)", dose: { r: [5, 6, 8] },
  },
  kapalbhati: {
    n: "Skull-shining breath", w: "chair", t: "breath",
    how: "Sit tall, hands on the knees. Breathe out sharply through the nose, pulling the belly in; let the in-breath come by itself. One a second, gently.",
    wrong: ["Forcing the out-breath", "Going on when you feel light-headed — stop and breathe normally"],
    // never with high BP, BP tablets or pregnancy (move-library §2.7, §3.1): the flags swap it down
    tags: ["chair", "breath"], flags: ["bp", "pregnant", "dizzy"], easier: "anulom",
    why: "so you finish the breathing awake and clear",
    hi: "कपालभाति", yoga: "Kapalbhati", dose: { t: [30, 45, 60] },
  },
};

/**
 * Kaya's calorie formula is `MET × kg × hours` on a 3.5 ml/kg/min resting rate; the Older Adult
 * Compendium's MET_60+ values assume 2.7, so its figures are multiplied by 0.77 before they reach
 * a move (move-library §3). A move carries the Kaya-scale value of the template kind it belongs to
 * — stretching and breath 2.3, seated work 2.4, standing strength, balance and cardio 2.9 — and a
 * walk the slowest band's 3.1, which the walk screen replaces by speed. The session formula is
 * the template's (ADR-052); this is only what a template reads when it has nothing better.
 */
function metFor(m: Raw): number {
  if (m.met !== undefined) return m.met;
  if (m.t === "mobility" || m.t === "breath") return 2.3;
  if (m.tags?.includes("chair")) return 2.4;
  return 2.9;
}

export const MOVES: Record<ExerciseId, Exercise> = Object.fromEntries(
  Object.entries(RAW).map(([id, m]) => [id, { ...m, id, yt: yt(m.n), met: metFor(m) }])
);

/** Every move: the reference's 48 and the library's 62. Read a move through `moveById`. */
export const EX_ALL: Record<ExerciseId, Exercise> = { ...EX, ...MOVES };

/* ---------- derived flags ---------- */

const POSITIONS: readonly MoveTag[] = ["chair", "standing", "support", "wall", "bed"];

/** The one position tag a move carries (every move has exactly one; a test pins it). */
export function position(ex: Exercise): MoveTag | null {
  return ex.tags?.find((t) => POSITIONS.includes(t)) ?? null;
}

const LOADED: readonly ExerciseKind[] = ["legs", "balance", "core", "upper", "cardio"];

/**
 * Three flags are read off the tags rather than written on every row, so a new move cannot forget
 * them (ADR-051):
 * - `footSore` — the guidance for numb feet or a foot sore is chair and bed only, so every move done
 *   on the feet (standing, at the counter, at the wall) carries it.
 * - `pregnant` — loaded flexion, impact, and core work lying on the back.
 * - `recent` — after an operation, a fracture or a cardiac event the plan holds at walks and the
 *   gentle sessions (plan §3.5), so every strength, balance and cardio move off the chair and the
 *   bed waits for a clinician; seated work, stretching, breath and the walk itself do not.
 */
export function derivedFlags(ex: Exercise): MoveFlag[] {
  const pos = position(ex);
  const tags = ex.tags ?? [];
  const out: MoveFlag[] = [];
  if (pos === "standing" || pos === "support" || pos === "wall") out.push("footSore");
  if (tags.includes("flexion") || tags.includes("impact") || (pos === "bed" && ex.t === "core")) {
    out.push("pregnant");
  }
  if (LOADED.includes(ex.t) && pos !== "chair" && ex.id !== "walk") out.push("recent");
  return out;
}

function union<T>(a: readonly T[] | undefined, b: readonly T[]): T[] {
  const out = [...(a ?? [])];
  for (const x of b) if (!out.includes(x)) out.push(x);
  return out;
}

/**
 * One move by id, with the annotations merged on, so a page, the queue and a template read one
 * object. The reference's 48 take theirs from `exerciseMeta.ts`; the library's carry their own.
 * Every move gains its derived flags. An unknown id is `undefined` — never a throw, because a
 * synced plan can name a move this build has not shipped yet.
 */
export function moveById(id: ExerciseId): Exercise | undefined {
  const own = Object.hasOwn(MOVES, id) ? MOVES[id] : undefined;
  if (own) {
    const video = VIDEO[id];
    return { ...own, ...(video ? { video } : {}), flags: union(own.flags, derivedFlags(own)) };
  }
  const base = Object.hasOwn(EX, id) ? EX[id] : undefined;
  if (!base) return undefined;
  const notes: Partial<Exercise> = {
    tags: TAGS[id],
    flags: FLAGS[id],
    easier: EASIER[id],
    harder: HARDER[id],
    why: WHY[id],
    hi: HI[id],
    yoga: YOGA[id],
    video: VIDEO[id],
  };
  const merged: Exercise = { ...base };
  // Only the annotations a move has, so a page can test `ex.hi` without meeting a key set to undefined.
  for (const [k, v] of Object.entries(notes)) if (v !== undefined) Object.assign(merged, { [k]: v });
  merged.flags = union(merged.flags, derivedFlags(merged));
  return merged;
}

/** Every move, merged, in a stable order: the reference's first, then the library's. */
export function allMoves(): Exercise[] {
  return Object.keys(EX_ALL).map((id) => moveById(id)).filter((m): m is Exercise => !!m);
}

/**
 * The words a search matches for a move: its English name, its Hindi name, and its yoga name with
 * and without the "-asana" run together, so "tadasana", "tada", "ताड़ासन" all find it — the food
 * table's `HOME_WORDS` idea, from the move's own fields.
 */
export function moveWords(ex: Exercise): string[] {
  const out = [ex.n.toLowerCase()];
  if (ex.hi) out.push(ex.hi.normalize("NFC"));
  if (ex.yoga) {
    const y = ex.yoga.toLowerCase();
    out.push(y, y.replace(/[\s–-]+/g, ""));
  }
  return out;
}

/** True when a typed query finds the move by any of its words. */
export function moveMatches(ex: Exercise, query: string): boolean {
  const q = query.trim().toLowerCase().normalize("NFC");
  if (!q) return true;
  return moveWords(ex).some((w) => w.includes(q));
}

/* ---------- the sheet's words ---------- */

function secs(n: number): string {
  return n >= 120 && n % 60 === 0 ? `${n / 60} min` : `${n} s`;
}

/**
 * A library move's three doses as words: "start at 5 reps, then 10, then 12"; a step that does not
 * change is said once ("3 reps, then 5"), and a walk counts in minutes.
 */
export function doseWords(d: MoveDose): string {
  const tuple = d.r ?? d.t ?? [];
  const steps = tuple.filter((n, i) => i === 0 || n !== tuple[i - 1]);
  if (!steps.length) return "";
  const first = d.r ? `${steps[0]} reps` : secs(steps[0]);
  const rest = steps.slice(1).map((n) => (d.t && n >= 120 && n % 60 === 0 ? `${n / 60}` : `${n}`));
  return `start at ${first}${rest.map((n) => `, then ${n}`).join("")}${d.side ? " each side" : ""}`;
}

/**
 * The library's dose line for a move: what today's session asks of it, or — for a move not in
 * today's session — where a library move starts. A reference move outside today has no line.
 */
export function libraryDoseLine(S: KayaState, ex: Exercise, date?: string): string | null {
  const key = sessionKeyFor(S, date);
  const s = sessionFor(key, S);
  for (const b of s?.blocks ?? []) {
    const it = items(S, b).find((x) => x.id === ex.id);
    if (it) return `In today’s session · ${doseText(it, doseIndex(S, key, date))}${it.r ? " reps" : ""}`;
  }
  return ex.dose ? `Not in today’s session · ${doseWords(ex.dose)}` : null;
}

/** A `how` as its steps, one sentence each, so the sheet can number them. */
export function howSteps(how: string): string[] {
  return how
    .split(/(?<=[.!?])\s+(?=[A-Z“"])/)
    .map((x) => x.trim())
    .filter(Boolean);
}
